import type { Vec2 } from '@/geometry/vec2';
import {
  add2,
  dot2,
  heading2,
  len2,
  mid2,
  normalize2,
  perp2,
  scale2,
  sub2,
} from '@/geometry/vec2';
import { pointInPolygon } from '@/geometry/polygon';
import { segmentIntersection } from '@/geometry/intersect';
import { GEOMETRY_EPSILON, PARALLEL_EPSILON } from '@/constants/numeric';

/**
 * Straight-edge-and-compass style construction tools.
 *
 * Each function returns an infinite line as (point, direction); callers clip it
 * to the sheet with `clipLineToPolygon` before turning it into a crease. Keeping
 * "what line is it" separate from "where does the paper end" is what lets the
 * same primitives serve the Huzita-Hatori axioms later, where several solutions
 * can exist and the user has to pick one.
 */
export interface Line {
  point: Vec2;
  direction: Vec2;
  /** Human-readable description, shown in the UI when several solutions exist. */
  label: string;
}

export const lineThroughPoints = (a: Vec2, b: Vec2): Line | null => {
  const d = sub2(b, a);
  if (len2(d) < GEOMETRY_EPSILON) return null;
  return { point: a, direction: normalize2(d), label: 'line through two points' };
};

export const perpendicularBisector = (a: Vec2, b: Vec2): Line | null => {
  const d = sub2(b, a);
  if (len2(d) < GEOMETRY_EPSILON) return null;
  return {
    point: mid2(a, b),
    direction: normalize2(perp2(d)),
    label: 'perpendicular bisector',
  };
};

export const parallelThrough = (point: Vec2, direction: Vec2): Line | null => {
  if (len2(direction) < GEOMETRY_EPSILON) return null;
  return { point, direction: normalize2(direction), label: 'parallel' };
};

export const perpendicularThrough = (point: Vec2, direction: Vec2): Line | null => {
  if (len2(direction) < GEOMETRY_EPSILON) return null;
  return { point, direction: normalize2(perp2(direction)), label: 'perpendicular' };
};

/**
 * The two angle bisectors of a pair of directions at a shared point. A pair of
 * lines always has two bisectors, at right angles to each other; both are
 * returned so the user chooses, rather than the tool guessing.
 */
export function angleBisectors(vertex: Vec2, dirA: Vec2, dirB: Vec2): Line[] {
  const a = normalize2(dirA);
  const b = normalize2(dirB);
  if (len2(a) < GEOMETRY_EPSILON || len2(b) < GEOMETRY_EPSILON) return [];
  const inner = normalize2(add2(a, b));
  if (len2(inner) < GEOMETRY_EPSILON) {
    // Anti-parallel: every perpendicular bisects, so return the perpendicular.
    return [{ point: vertex, direction: perp2(a), label: 'bisector (anti-parallel)' }];
  }
  return [
    { point: vertex, direction: inner, label: 'interior bisector' },
    { point: vertex, direction: perp2(inner), label: 'exterior bisector' },
  ];
}

/** Reflect a point across the line through `point` with direction `direction`. */
export function reflectPoint(p: Vec2, line: Line): Vec2 {
  const d = normalize2(line.direction);
  const v = sub2(p, line.point);
  const along = scale2(d, dot2(v, d));
  const perpComponent = sub2(v, along);
  return add2(line.point, sub2(along, perpComponent));
}

/**
 * Clip an infinite line to a (possibly non-convex) polygon.
 *
 * All crossings with the polygon boundary are collected, sorted along the line,
 * and consecutive pairs are kept when their midpoint is inside. That handles
 * concave outlines and holes-free multi-part intersections correctly, which a
 * simple two-point clip would not.
 */
export function clipLineToPolygon(line: Line, polygon: readonly Vec2[]): [Vec2, Vec2][] {
  if (polygon.length < 3) return [];
  const d = normalize2(line.direction);
  if (len2(d) < GEOMETRY_EPSILON) return [];

  // A segment guaranteed to span the polygon.
  let extent = 0;
  for (const p of polygon) extent = Math.max(extent, len2(sub2(p, line.point)));
  const reach = extent * 2 + 1;
  const from = add2(line.point, scale2(d, -reach));
  const to = add2(line.point, scale2(d, reach));

  const params: number[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const hit = segmentIntersection(from, to, a, b);
    if (hit.kind === 'point' && hit.t !== undefined) params.push(hit.t);
    else if (hit.kind === 'overlap' && hit.overlap) params.push(hit.overlap[0], hit.overlap[1]);
  }
  if (params.length < 2) return [];
  params.sort((x, y) => x - y);

  const out: [Vec2, Vec2][] = [];
  const at = (t: number): Vec2 => add2(from, scale2(sub2(to, from), t));
  for (let i = 0; i + 1 < params.length; i++) {
    const t0 = params[i];
    const t1 = params[i + 1];
    if (t1 - t0 < GEOMETRY_EPSILON) continue;
    const midpoint = at((t0 + t1) / 2);
    if (!pointInPolygon(midpoint, polygon)) continue;
    out.push([at(t0), at(t1)]);
  }
  return out;
}

/**
 * Clip a finite segment to a polygon, keeping only the parts inside.
 *
 * Used when the user draws a crease that runs off the edge of the paper: the
 * part beyond the sheet is not a crease, and keeping it would leave stray
 * geometry that the analyser then (correctly) complains about.
 */
export function clipSegmentToPolygon(
  a: Vec2,
  b: Vec2,
  polygon: readonly Vec2[],
): [Vec2, Vec2][] {
  if (polygon.length < 3) return [[a, b]];
  const params: number[] = [0, 1];
  for (let i = 0; i < polygon.length; i++) {
    const q1 = polygon[i];
    const q2 = polygon[(i + 1) % polygon.length];
    const hit = segmentIntersection(a, b, q1, q2);
    if (hit.kind === 'point' && hit.t !== undefined) params.push(hit.t);
    else if (hit.kind === 'overlap' && hit.overlap) params.push(hit.overlap[0], hit.overlap[1]);
  }
  params.sort((x, y) => x - y);

  const at = (t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  const out: [Vec2, Vec2][] = [];
  for (let i = 0; i + 1 < params.length; i++) {
    const t0 = Math.max(0, params[i]);
    const t1 = Math.min(1, params[i + 1]);
    if (t1 - t0 < 1e-9) continue;
    const midpoint = at((t0 + t1) / 2);
    // The boundary itself counts as inside: a crease drawn along the sheet edge
    // is a legitimate (if redundant) thing to draw.
    if (!pointInPolygon(midpoint, polygon) && !onPolygonBoundary(midpoint, polygon)) continue;
    out.push([at(t0), at(t1)]);
  }
  return out;
}

function onPolygonBoundary(p: Vec2, polygon: readonly Vec2[]): boolean {
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const hit = segmentIntersection(p, p, a, b);
    if (hit.kind === 'point') return true;
  }
  return false;
}

/** Intersection of two construction lines, if they are not parallel. */
export function lineIntersection(a: Line, b: Line): Vec2 | null {
  const cross = a.direction.x * b.direction.y - a.direction.y * b.direction.x;
  if (Math.abs(cross) < PARALLEL_EPSILON) return null;
  const diff = sub2(b.point, a.point);
  const t = (diff.x * b.direction.y - diff.y * b.direction.x) / cross;
  return add2(a.point, scale2(a.direction, t));
}

export const lineAngleDegrees = (line: Line): number =>
  (heading2(line.direction) * 180) / Math.PI;
